import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { LucideIcon, Rocket, Wrench, Users as UsersIcon } from 'lucide-react';

interface ToolCardProps {
  icon: LucideIcon;
  name: string;
  status: 'available' | 'in-development' | 'planned';
  supportLevel: 'self-service' | 'setup-required' | 'consultation';
  problem: string;
  description: string;
  tags?: string[];
  image?: string;
  onClick?: () => void;
}

export function ToolCard({
  icon: Icon,
  name,
  status,
  supportLevel,
  problem,
  description,
  tags,
  image,
  onClick,
}: ToolCardProps) {
  const isAvailable = status === 'available';
  const isInDevelopment = status === 'in-development';

  // Opacity for entire card
  const cardOpacity = isAvailable ? 1 : isInDevelopment ? 0.7 : 0.5;

  // Status badge styling
  const statusStyles = {
    available: {
      backgroundColor: '#BEE0C1',
      color: '#51BA6D',
      border: '1px solid #8BCC96',
      label: 'Available Now',
    },
    'in-development': {
      backgroundColor: '#F7FAE9',
      color: '#A5CE39',
      border: '1px solid #C0DB77',
      label: 'In Development',
    },
    planned: {
      backgroundColor: '#E6E7E7',
      color: '#7C8081',
      border: '1px solid #C0C2C3',
      label: 'Planned',
    },
  };

  // Support level badge styling
  const supportStyles = {
    'self-service': {
      backgroundColor: '#BEE0C1',
      color: '#51BA6D',
      border: '1px solid #8BCC96',
      label: 'Self-Service',
      icon: Rocket,
    },
    'setup-required': {
      backgroundColor: '#FFEDD5',
      color: '#9A3412',
      border: '1px solid #FED7AA',
      label: 'Setup Required',
      icon: Wrench,
    },
    consultation: {
      backgroundColor: '#FAF5FF',
      color: '#9333EA',
      border: '1px solid #E9D5FF',
      label: 'Consultation',
      icon: UsersIcon,
    },
  };

  const SupportIcon = supportStyles[supportLevel].icon;

  return (
    <div
      className="bg-white rounded-xl p-4 md:p-6 transition-all duration-200 ease-out h-full"
      style={{
        border: isAvailable ? '2px solid #8BCC96' : '1px solid #E5E7EB',
        boxShadow: isAvailable
          ? '0 4px 6px rgba(81, 186, 109, 0.1)'
          : '0 2px 4px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        opacity: cardOpacity,
        cursor: isAvailable ? 'default' : 'default',
      }}
      onMouseEnter={(e) => {
        if (isAvailable && window.innerWidth >= 768) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 12px rgba(81, 186, 109, 0.15)';
          e.currentTarget.style.borderColor = '#70C282';
        }
      }}
      onMouseLeave={(e) => {
        if (isAvailable && window.innerWidth >= 768) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 6px rgba(81, 186, 109, 0.1)';
          e.currentTarget.style.borderColor = '#8BCC96';
        }
      }}
    >
      {/* Icon or Image */}
      <div
        className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center overflow-hidden mb-3 md:mb-4"
        style={{ backgroundColor: image ? 'transparent' : '#EFF7EF' }}
      >
        {image ? (
          <img src={image} alt={name} className="w-full h-full object-cover" />
        ) : (
          <Icon size={20} className="md:w-6 md:h-6" style={{ color: '#51BA6D' }} />
        )}
      </div>

      {/* Tool name and status */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="flex-1 text-base md:text-lg font-semibold text-sb-black leading-tight">
          {name}
        </h3>
        <Badge
          className="flex-shrink-0 text-xs"
          style={{
            ...statusStyles[status],
            fontSize: '11px',
            fontWeight: '500',
            lineHeight: '1.4',
          }}
        >
          {statusStyles[status].label}
        </Badge>
      </div>

      {/* Support level badge */}
      <div className="mb-3 md:mb-4">
        <Badge
          className="flex items-center gap-1 w-fit text-xs"
          style={{
            ...supportStyles[supportLevel],
            fontSize: '11px',
            fontWeight: '500',
            lineHeight: '1.4',
          }}
        >
          <SupportIcon size={11} />
          {supportStyles[supportLevel].label}
        </Badge>
      </div>

      {/* Problem statement - BOLD */}
      <p className="text-sm md:text-base font-bold text-sb-black mb-2 line-clamp-2">
        {problem}
      </p>

      {/* Description */}
      <p className="flex-1 text-sm text-sb-grey leading-relaxed mb-3 md:mb-4 line-clamp-3">
        {description}
      </p>

      {/* Tags */}
      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 md:gap-2 mb-3 md:mb-4">
          {tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="text-xs"
              style={{
                backgroundColor: '#F3F4F6',
                color: '#6B7280',
                fontSize: '11px',
                fontWeight: '500',
              }}
            >
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="space-y-2 mt-auto">
        <Button
          className="w-full transition-all duration-150 ease touch-manipulation"
          disabled={!isAvailable}
          onClick={onClick}
          style={{
            backgroundColor: isAvailable ? '#51BA6D' : '#E6E7E7',
            color: isAvailable ? 'white' : '#9CA0A1',
            border: isAvailable ? 'none' : '1px solid #C0C2C3',
            padding: '10px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: isAvailable ? '0 2px 4px rgba(81, 186, 109, 0.2)' : 'none',
            cursor: isAvailable ? 'pointer' : 'not-allowed',
            opacity: isAvailable ? 1 : 0.7,
            minHeight: '44px', // iOS touch target minimum
          }}
          onMouseEnter={(e) => {
            if (isAvailable && window.innerWidth >= 768) {
              e.currentTarget.style.backgroundColor = '#70C282';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(81, 186, 109, 0.25)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (isAvailable && window.innerWidth >= 768) {
              e.currentTarget.style.backgroundColor = '#51BA6D';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(81, 186, 109, 0.2)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          {isAvailable ? 'Launch Tool →' : isInDevelopment ? 'Coming Soon' : 'Planned'}
        </Button>
        {/* Secondary CTAs hidden until content is ready */}
        {/* {isAvailable && (
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="transition-all duration-150 ease"
              style={{
                backgroundColor: 'transparent',
                borderColor: '#AAC6D2',
                color: '#068AA1',
                padding: '12px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#EBEFF2';
                e.currentTarget.style.borderColor = '#8CB4C4';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#AAC6D2';
              }}
            >
              View Examples
            </Button>
            <Button
              variant="outline"
              className="transition-all duration-150 ease"
              style={{
                backgroundColor: 'transparent',
                borderColor: '#AAC6D2',
                color: '#068AA1',
                padding: '12px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#EBEFF2';
                e.currentTarget.style.borderColor = '#8CB4C4';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#AAC6D2';
              }}
            >
              Tutorial
            </Button>
          </div>
        )} */}
      </div>
    </div>
  );
}
